// src/odoo/customers/hooks/useCustomerOverview.ts
// All comments are in English.

import { useQuery } from "@tanstack/react-query";

import {
  fetchCustomerOverview,
  fetchPurchaseTrend,
  fetchPaymentSummary,
  fetchUtmSummary,
  fetchCustomerContacts,
  type DateRangeQuery,
  type CustomerOverview,
  type PurchaseTrendResponse,
  type PurchaseTrendPeriod,
  type PaymentSummaryResponse,
  type UtmSummaryResponse,
  type CustomerContactsResponse,
} from "../api/overviewApi";

// Root query key for all customer-related queries.
export const CUSTOMERS_QUERY_KEY = "customers";

/**
 * Overview KPI for a company-level customer.
 */
export function useCustomerOverview(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<CustomerOverview>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "overview",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchCustomerOverview(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * Purchase trend (e.g. by month/day) for a company-level customer.
 */
export function usePurchaseTrend(
  companyPartnerId?: number,
  period: PurchaseTrendPeriod = "month",
  dateRange?: DateRangeQuery
) {
  return useQuery<PurchaseTrendResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "purchase-trend",
      companyPartnerId,
      period,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchPurchaseTrend(companyPartnerId as number, period, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * Payment summary and aging buckets for a company-level customer.
 */
export function usePaymentSummary(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<PaymentSummaryResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "payment-summary",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchPaymentSummary(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * UTM (source/medium/campaign) summary for a company-level customer.
 */
export function useUtmSummary(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<UtmSummaryResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "utm-summary",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () => fetchUtmSummary(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}

/**
 * Contacts under a company with basic stats (orders + GMV).
 */
export function useCustomerContacts(
  companyPartnerId?: number,
  dateRange?: DateRangeQuery
) {
  return useQuery<CustomerContactsResponse>({
    queryKey: [
      CUSTOMERS_QUERY_KEY,
      "contacts",
      companyPartnerId,
      dateRange ?? null,
    ],
    queryFn: () =>
      fetchCustomerContacts(companyPartnerId as number, dateRange),
    enabled: !!companyPartnerId,
  });
}
